import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {shallowEqual} from 'react-redux';
import * as THREE from 'three';
import {useAppSelector} from 'store/customer';
import {
    getRoomDimensions,
    getRoomType,
    getScale,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import type {RoomConfig} from 'components/customer/RoomPlanner/helpers/measurement/types';
import type {DistanceBubbleRect} from 'components/customer/RoomPlanner/helpers/createRoomObjects';
import {DISTANCE_LABEL_STYLE} from 'components/customer/RoomPlanner/helpers/measurement/distanceLabelStyle';
import {
    HorizontalWall,
    isAllowedDistanceInput,
    parseWallDistanceInput,
    refreshMovementOverlays,
    resolveWallDistancePlacement,
} from 'components/customer/RoomPlanner/helpers/measurement/wallDistanceEdit';
import useAutoSave from 'components/customer/RoomPlanner/lib/useAutoSave';
import CameraSetup from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import {
    isScene,
    syncOverlaysOnCabinetMove,
} from 'components/customer/RoomPlanner/helpers/syncOverlayOnCabinetMove';

// On-screen pill proportions, from the canvas design (drawn at 2x).
const {canvasHeight} = DISTANCE_LABEL_STYLE;
const FONT_RATIO = DISTANCE_LABEL_STYLE.fontSize / canvasHeight;
const PADDING_RATIO = DISTANCE_LABEL_STYLE.paddingX / canvasHeight;
const RADIUS_RATIO = DISTANCE_LABEL_STYLE.radius / canvasHeight;
const BORDER_RATIO = DISTANCE_LABEL_STYLE.borderWidth / canvasHeight;

export interface DistanceBubbleEdit extends DistanceBubbleRect {
    wall: HorizontalWall;
    value: string;
}

interface DistanceBubbleEditorProps {
    edit: DistanceBubbleEdit;
    cabinet: CabinetMesh;
    cabinets: CabinetMesh[];
    onClose: () => void;
}

/**
 * Lets the user type a new wall distance straight into its pill label.
 * Enter applies the move (after validation); Escape or focusing out
 * restores the previous value.
 * @return {JSX.Element}
 */
const DistanceBubbleEditor = ({
    edit,
    cabinet,
    cabinets,
    onClose,
}: DistanceBubbleEditorProps) => {
    const scale = useAppSelector(getScale);
    const roomType = useAppSelector(getRoomType);
    const {rectangularDimension, lShapedDimension} = useAppSelector(
        getRoomDimensions,
        shallowEqual
    );
    const {debouncedSave} = useAutoSave();

    const inputRef = useRef<HTMLInputElement>(null);
    // Set once the edit ends, so the blur that follows Enter / Escape
    // doesn't close it a second time.
    const isClosedRef = useRef(false);
    const [value, setValue] = useState(edit.value);
    const [error, setError] = useState<string | null>(null);

    const close = useCallback(() => {
        if (isClosedRef.current) return;
        isClosedRef.current = true;
        onClose();
    }, [onClose]);

    // The input stands in for the pill while editing. The pill keeps its
    // hover zoom meanwhile: isEditing stops the hover tracker shrinking it
    // out of sight when the pointer leaves the input.
    useEffect(() => {
        const {sprite} = edit;
        const userData = sprite.userData as {
            isEditing?: boolean;
            isHovered?: boolean;
            onHoverEnd?: () => void;
        };
        userData.isEditing = true;
        sprite.visible = false;

        inputRef.current?.focus();
        inputRef.current?.select();

        return () => {
            userData.isEditing = false;

            // Skip if the labels were rebuilt (e.g. after the move).
            if (!sprite.parent) return;

            // Reappear zoomed in, then ease back out. If the pointer is
            // still over it, the hover tracker zooms it back in.
            sprite.visible = true;
            userData.isHovered = false;
            userData.onHoverEnd?.();
        };
    }, [edit]);

    // The input is fixed in place, so orbiting or zooming would leave it
    // floating off its line: treat that as cancelling.
    useEffect(() => {
        const {controls} = CameraSetup;
        if (!controls) return;

        controls.addEventListener('start', close);
        return () => controls.removeEventListener('start', close);
    }, [close]);

    const applyDistance = useCallback(() => {
        const parsed = parseWallDistanceInput(value);
        if ('error' in parsed) {
            setError(parsed.error);
            return;
        }

        // Unchanged: nothing to move or save.
        if (parsed.mm === Number(edit.value)) {
            close();
            return;
        }

        const roomConfig: RoomConfig = {
            roomType: roomType ?? 'RECTANGULAR',
            rectangularDimension: {
                width: rectangularDimension.width,
                length: rectangularDimension.length,
                height: rectangularDimension.height,
            },
            lShapedDimension: {
                rightWidth: lShapedDimension.rightWidth,
                leftWidth: lShapedDimension.leftWidth,
                rightDepth: lShapedDimension.rightDepth,
                leftDepth: lShapedDimension.leftDepth,
                height: lShapedDimension.height,
            },
            scale,
        };

        const placement = resolveWallDistancePlacement(
            cabinet,
            edit.wall,
            parsed.mm,
            cabinets,
            roomConfig
        );
        if ('error' in placement) {
            setError(placement.error);
            return;
        }
        const {position} = placement;

        const {move} = cabinet.userData as {
            move?: (position: THREE.Vector3) => void;
        };
        if (typeof move === 'function') {
            move(position);
        } else {
            cabinet.position.copy(position);
        }
        cabinet.updateMatrixWorld(true);

        refreshMovementOverlays(cabinet);
        const scene = cabinet.parent;
        if (cabinet.userData?.isPlaceholder && isScene(scene)) {
            // Placeholder move does not rebuild measurement geometry on its
            // own. Cabinets already redraw from their own move.
            syncOverlaysOnCabinetMove(cabinet, scene);
        }
        debouncedSave(cabinet);
        close();
    }, [
        value,
        edit,
        cabinet,
        cabinets,
        roomType,
        rectangularDimension,
        lShapedDimension,
        scale,
        debouncedSave,
        close,
    ]);

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const next = event.target.value;
            if (!isAllowedDistanceInput(next)) return;

            setValue(next);
            setError(null);
        },
        []
    );

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                applyDistance();
            } else if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                close();
            }
        },
        [applyDistance, close]
    );

    // Stay at the hovered (zoomed-in) size the pill had when clicked.
    const height = edit.height * DISTANCE_LABEL_STYLE.hoverScale;
    // Match the text width to the pill: monospace digits are ~0.6em wide.
    const fontSize = height * FONT_RATIO;
    const textWidth = Math.max(value.length, 2) * fontSize * 0.6;

    return (
        <Wrapper style={{left: edit.centerX, top: edit.centerY}}>
            <BubbleInput
                ref={inputRef}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                aria-label={`Distance from ${edit.wall} wall in mm`}
                aria-invalid={error !== null}
                $hasError={error !== null}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onBlur={close}
                style={{
                    height,
                    width: textWidth + height * PADDING_RATIO * 2,
                    fontSize,
                    padding: `0 ${height * PADDING_RATIO}px`,
                    borderRadius: height * RADIUS_RATIO,
                    borderWidth: Math.max(1, height * BORDER_RATIO),
                }}
            />
            {error ? <ErrorMessage role="alert">{error}</ErrorMessage> : null}
        </Wrapper>
    );
};

export default DistanceBubbleEditor;

const Wrapper = styled.div`
    position: fixed;
    z-index: 2000;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const BubbleInput = styled.input<{$hasError: boolean}>`
    box-sizing: border-box;
    background: ${DISTANCE_LABEL_STYLE.fill};
    border-style: solid;
    border-color: ${({$hasError}) =>
        $hasError ? '#E5484D' : DISTANCE_LABEL_STYLE.border};
    color: ${DISTANCE_LABEL_STYLE.text};
    font-family: ${DISTANCE_LABEL_STYLE.fontFamily};
    font-weight: ${DISTANCE_LABEL_STYLE.fontWeight};
    line-height: 1;
    text-align: center;
    outline: none;
    box-shadow: 0 0 0 3px
        ${({$hasError}) =>
            $hasError ? 'rgba(229, 72, 77, 0.25)' : 'rgba(69, 170, 242, 0.35)'};
`;

const ErrorMessage = styled.div`
    position: absolute;
    top: calc(100% + 6px);
    white-space: nowrap;
    background: #e5484d;
    color: #fff;
    font-size: 12px;
    line-height: 1.3;
    padding: 3px 8px;
    border-radius: 4px;
    pointer-events: none;
`;
